/**
 * Pixel de site opt-in — GET/PATCH /api/v1/integrations/site-pixel (DOC 08 § 4.1).
 * Por quê: desligado por padrão; Carla cola o snippet só se quiser.
 */

import { APIClient } from "./api_helper";

const api = new APIClient();

export type SitePixelConfig = {
  enabled: boolean;
  public_key: string | null;
  snippet: string | null;
  script_url: string | null;
  purge_days: number;
  pageview_tag_names: string;
  identify_tag_names: string;
  events_map: Array<{ event_name: string; tag_names: string }>;
};

export type UpdateSitePixelPayload = {
  enabled?: boolean;
  purge_days?: number;
  pageview_tag_names?: string | null;
  identify_tag_names?: string | null;
  events_map?: Array<{ event_name: string; tag_names: string }>;
};

type ApiSitePixel = {
  enabled?: boolean;
  public_key?: string | null;
  snippet?: string | null;
  script_url?: string | null;
  purge_days?: number;
  config?: {
    pageview?: { tag_names?: string[]; fields?: Record<string, string> };
    identify?: { tag_names?: string[]; fields?: Record<string, string> };
    events?: Record<string, { tag_names?: string[]; fields?: Record<string, string> }>;
  };
};

const csv = (tags: string[] | undefined): string =>
  Array.isArray(tags) ? tags.filter(Boolean).join(", ") : "";

const splitCsv = (raw: string | null | undefined): string[] =>
  (raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const mapFromApi = (raw: ApiSitePixel | null | undefined): SitePixelConfig => {
  const cfg = raw?.config || {};
  const events = cfg.events || {};
  const events_map = Object.entries(events).map(([event_name, v]) => ({
    event_name,
    tag_names: csv(v?.tag_names),
  }));

  return {
    enabled: Boolean(raw?.enabled),
    public_key: raw?.public_key ?? null,
    snippet: raw?.snippet ?? null,
    script_url: raw?.script_url ?? null,
    purge_days: Number(raw?.purge_days) > 0 ? Number(raw?.purge_days) : 90,
    pageview_tag_names: csv(cfg.pageview?.tag_names),
    identify_tag_names: csv(cfg.identify?.tag_names),
    events_map,
  };
};

const mapToApi = (payload: UpdateSitePixelPayload) => {
  const events: Record<string, { tag_names: string[]; fields: Record<string, string> }> =
    {};
  for (const row of payload.events_map || []) {
    const name = row.event_name.trim().toLowerCase();
    if (!name) {
      continue;
    }
    events[name] = {
      tag_names: splitCsv(row.tag_names),
      fields: {},
    };
  }

  return {
    enabled: payload.enabled,
    purge_days: payload.purge_days,
    config: {
      pageview: {
        tag_names: splitCsv(payload.pageview_tag_names),
        fields: {},
      },
      identify: {
        tag_names: splitCsv(payload.identify_tag_names),
        fields: {},
      },
      events,
    },
  };
};

export const fetchSitePixel = async (): Promise<{
  success: boolean;
  data?: SitePixelConfig;
  message?: string;
  error?: { message?: string };
}> => {
  const envelope: any = await api.get("/integrations/site-pixel");
  if (!envelope?.success) {
    return envelope;
  }
  return {
    success: true,
    data: mapFromApi(envelope.data?.site_pixel),
    message: envelope.message,
  };
};

export const updateSitePixel = async (
  payload: UpdateSitePixelPayload
): Promise<{
  success: boolean;
  data?: SitePixelConfig;
  message?: string;
  error?: { message?: string };
}> => {
  const envelope: any = await api.update(
    "/integrations/site-pixel",
    mapToApi(payload)
  );
  if (!envelope?.success) {
    return envelope;
  }
  return {
    success: true,
    data: mapFromApi(envelope.data?.site_pixel),
    message: envelope.message,
  };
};
